import { Star } from "lucide-react";
import { AppIcon } from "@/components/AppIcon";
import { HealthDot } from "@/components/HealthDot";
import { cn } from "@/lib/cn";
import type { DirectoryApp } from "@/lib/apps";
import type { HealthStatus } from "@/lib/directory";

/**
 * ⚠ No access badge, and none of the three it used to show.
 *
 * The grid now renders only apps you can sign in to, which retires all three states
 * rather than just hiding them:
 *
 *   - "Suspended there" can never appear — a suspension hides the card.
 *   - "No access" can only appear on an app open to every employee, whose endpoint
 *     reports `registered: false` for anyone who has not signed in yet. There the
 *     badge is simply WRONG: they can walk straight in.
 *   - "You have access" is true of every card on the page, which makes it noise.
 *
 * The card therefore no longer takes the access answer at all. Directory still
 * fetches it — that is what drives the filter — it just has nothing left to say here.
 */
interface Props {
  app: DirectoryApp;
  health: HealthStatus;
  onToggleFavourite: (app: DirectoryApp) => void;
  onLaunch: (app: DirectoryApp) => void;
}

/**
 * Icon and title, nothing else.
 *
 * Used for the two personal rows — favourites and "Jump back in" — where every
 * app is one the user has already chosen or opened: they know what it is, and
 * a full card repeated the same description a few rows below. Rendering these
 * as chips rather than smaller cards is deliberate; at a glance the row reads
 * as shortcuts, not as another shelf of apps.
 *
 * `onToggleFavourite` is optional, and passing it is what puts a star on the
 * chip. Favourites need one — without it there is no way to unstar from the
 * row that exists *because* you starred something. History does not: the same
 * app carries a star in its own section, and two toggles for one app invites
 * the "did that register?" double-click.
 */
export function CompactAppCard({
  app,
  onLaunch,
  onToggleFavourite,
}: {
  app: DirectoryApp;
  onLaunch: (app: DirectoryApp) => void;
  onToggleFavourite?: (app: DirectoryApp) => void;
}) {
  return (
    <div
      className={cn(
        "group border-border bg-card relative flex items-center gap-2.5 rounded-lg border py-2 pl-2.5",
        "hover:border-primary/40 transition-colors",
        onToggleFavourite ? "pr-1.5" : "pr-4",
      )}
    >
      <AppIcon title={app.title} iconPath={app.iconPath} size={26} />
      <a
        href={app.url}
        onClick={() => onLaunch(app)}
        className="group-hover:text-primary truncate text-sm font-medium transition-colors after:absolute after:inset-0 after:rounded-lg"
      >
        {app.title}
      </a>

      {/* Outside the anchor, above its overlay — same reasoning as the full
          card: no interactive element nested inside a link. */}
      {onToggleFavourite && (
        <button
          type="button"
          onClick={() => onToggleFavourite(app)}
          aria-pressed={app.favourite}
          aria-label={
            app.favourite ? `Unfavourite ${app.title}` : `Favourite ${app.title}`
          }
          className={cn(
            "relative z-10 grid h-6 w-6 shrink-0 place-items-center rounded transition-colors",
            app.favourite ? "text-warning" : "text-muted-foreground/40",
          )}
        >
          <Star size={14} fill={app.favourite ? "currentColor" : "none"} />
        </button>
      )}
    </div>
  );
}

export function AppCard({ app, health, onToggleFavourite, onLaunch }: Props) {
  return (
    <div
      className={cn(
        "group border-border bg-card relative flex gap-3 rounded-lg border p-4",
        "transition-shadow hover:shadow-sm focus-within:shadow-sm",
      )}
    >
      <AppIcon title={app.title} iconPath={app.iconPath} />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {/*
            The link's ::after covers the whole card, so the entire tile is
            clickable while the accessible name stays just the app title.
          */}
          <a
            href={app.url}
            onClick={() => onLaunch(app)}
            className={cn(
              "text-card-foreground font-medium after:absolute after:inset-0 after:rounded-lg",
              "group-hover:text-primary transition-colors",
            )}
          >
            {app.title}
          </a>
          <HealthDot status={health} />
        </div>

        {/* The description is not clamped: cut off at two lines with an
            ellipsis it told you an app existed but not what it did, which is
            the one job the line has. Cards in a row stretch to the tallest, so
            the grid stays aligned. */}
        {app.description ? (
          <p className="text-muted-foreground mt-1 text-sm">{app.description}</p>
        ) : (
          <p className="text-muted-foreground/70 mt-1 text-sm italic">
            No description yet
          </p>
        )}
      </div>

      {/*
        Above the link overlay so starring never navigates, and outside the
        anchor entirely so no interactive element nests inside a link.
      */}
      <button
        type="button"
        onClick={() => onToggleFavourite(app)}
        aria-pressed={app.favourite}
        aria-label={app.favourite ? `Unfavourite ${app.title}` : `Favourite ${app.title}`}
        className={cn(
          "relative z-10 -m-1 h-8 w-8 shrink-0 self-start rounded-md",
          "grid place-items-center transition-colors",
          app.favourite
            ? "text-warning"
            : "text-muted-foreground/40 hover:text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
        )}
      >
        <Star size={17} fill={app.favourite ? "currentColor" : "none"} />
      </button>
    </div>
  );
}
